import { DEFAULT_CURRENCY, getCurrencyInfo } from '@/configs/currencies'
import { NOT_A_NUMBER_LABEL } from '@/configs/ui'
import { useSsrContext } from '@/contexts/ssr'
import { useExchangeRates } from '@/services/currency'
import { Currency } from '@/types/global'
import { fiatCurrencyFormat, formatCurrencyAmount, formatMarketCap } from '@/utils/number/format'

// More flexible type to accommodate different formatter signatures
type ValueFormatter =
  | ((
      val: number | string,
      options?: { separateThousands?: boolean; showNegativeSign?: boolean },
    ) => string)
  | ((val: number, options?: FormatTokenPriceOptions) => string)
  | ((value?: number, ...args: unknown[]) => string)

type Amount = undefined | number | AmountObject<undefined> | AmountObject<number>

type Amounts = Amount[]
type AmountObject<T> = {
  value?: T
  currency?: Currency
  tokenSymbol?: string
  valueFormatter?: ValueFormatter
  currencyFormatOptions?: FormatTokenPriceOptions
  isUsedForTokenPrice?: boolean
  isExchangeRateBasedOnSymbolCurrency?: boolean
}
type UseCurrencyAmountsOptions = {
  amounts: Amounts
  tokenSymbol?: string
  currency?: Currency
  valueFormatter?: ValueFormatter
  currencyFormatOptions?: FormatTokenPriceOptions
  isUsedForTokenPrice?: boolean
  isExchangeRateBasedOnSymbolCurrency?: boolean
}

export type FormatTokenPriceOptions = {
  // Show code instead if symbol empty
  symbolOrCode?: boolean
  showSymbol?: boolean
  showCode?: boolean
}

type CurrencyNumberFormatResult = {
  value: number | undefined
  formattedValue: string
}

type ExchangeRatesData = ReturnType<typeof useExchangeRates>['data']

// Pure function to check if value is valid number
// Returns true if value is a number and not NaN
// Example: isValidNumber(123) => true, isValidNumber(NaN) => false, isValidNumber("abc") => false
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isValidNumber = (value: any): value is number =>
  typeof value === 'number' && !Number.isNaN(value)

// Pure function to calculate exchange rate between currencies
// Calculates the conversion rate from token currency to target currency
// Handles missing exchange rates gracefully by returning undefined
// Example:
// - calculateExchangeRate({ exchangeRates: { USD: 1, BTC: 0.00004 }, targetCurrency: { code: 'USD' }, tokenSymbol: 'BTC' }) => 25000
// - calculateExchangeRate({ exchangeRates: { USD: 1, BTC: 0.00004 }, targetCurrency: { code: 'BTC' }, tokenSymbol: 'BTC' }) => 1
// - calculateExchangeRate({ exchangeRates: { USD: 1 }, targetCurrency: { code: 'EUR' }, tokenSymbol: 'BTC' }) => undefined (missing rates)
const calculateExchangeRate = ({
  exchangeRates,
  targetCurrency,
  tokenSymbol,
  isExchangeRateBasedOnSymbolCurrency,
}: {
  exchangeRates: ExchangeRatesData
  targetCurrency: Currency
  tokenSymbol?: string
  isExchangeRateBasedOnSymbolCurrency?: boolean
}): number | undefined => {
  if (!exchangeRates || !targetCurrency) return undefined

  // Check if target currency exists in exchange rates
  const targetRate = exchangeRates[targetCurrency.code as keyof typeof exchangeRates]
  if (targetRate === undefined) return undefined

  let exchangeRate = targetRate

  if (isExchangeRateBasedOnSymbolCurrency && tokenSymbol) {
    // Check if token symbol exists in exchange rates
    const tokenRate = exchangeRates[tokenSymbol as keyof typeof exchangeRates]
    if (tokenRate === undefined) return undefined

    exchangeRate = targetCurrency.code === tokenSymbol ? 1 : targetRate / tokenRate
  }

  return exchangeRate
}

// Pure function to convert amount based on exchange rate
// Multiplies the value by the exchange rate to get converted amount
// Example: convertAmount(100, 1.5) => 150 (converts 100 USD to EUR at 1.5 rate)
const convertAmount = (value: number, exchangeRate: number): number => value * exchangeRate

// Pure function to format currency value with proper error handling
// Returns formatted string or "N/A" for invalid numbers
// Example: formatCurrencyValue(123.45, formatPrice) => "$123.45"
// Example: formatCurrencyValue(NaN, formatPrice) => "N/A"
const formatCurrencyValue = (value: number | undefined, formatter: ValueFormatter): string => {
  if (!isValidNumber(value)) return NOT_A_NUMBER_LABEL
  if (Number.isNaN(value)) return NOT_A_NUMBER_LABEL
  return formatter(value)
}

// Pure function to process amount value with currency conversion and special token price logic
// Handles token price normalization (returns 1 when token symbol matches target currency)
// Example:
// - processAmountValue({ value: 100, exchangeRates: { USD: 1, BTC: 0.00004 }, currency: { code: 'USD' }, tokenSymbol: 'BTC' }) => 2500000
// - processAmountValue({ value: 100, currency: { code: 'BTC' }, tokenSymbol: 'BTC', isUsedForTokenPrice: true }) => 1
const processAmountValue = ({
  value,
  exchangeRates,
  currency,
  tokenSymbol,
  isExchangeRateBasedOnSymbolCurrency,
  isUsedForTokenPrice,
  formatter = fiatCurrencyFormat,
}: {
  value: number | undefined
  exchangeRates: ExchangeRatesData
  currency: Currency
  tokenSymbol?: string
  isExchangeRateBasedOnSymbolCurrency?: boolean
  isUsedForTokenPrice?: boolean
  formatter?: ValueFormatter
}): number | undefined => {
  if (!isValidNumber(value)) return value

  // If cryptocurrencies are consistent, keeps the price value 1
  if (
    tokenSymbol &&
    currency &&
    tokenSymbol === currency.code &&
    formatter !== formatMarketCap &&
    isUsedForTokenPrice
  ) {
    return 1
  }

  // Fallback to USD amounts if exchange rates are not ready
  if (!exchangeRates) {
    return value
  }

  // Calculate target currency amount
  const exchangeRate = calculateExchangeRate({
    exchangeRates,
    targetCurrency: currency,
    tokenSymbol,
    isExchangeRateBasedOnSymbolCurrency,
  })

  if (exchangeRate) {
    return convertAmount(value, exchangeRate)
  }

  // If there is no target exchange rate, return NaN
  return Number.NaN
}

// Pure function to create currency number format result with optional currency formatting
// Combines amount processing and formatting into a single result object
// Example:
// - createCurrencyNumberFormat({ value: 100, formatter: formatPrice, currency: { code: 'USD' } }) => { value: 100, formattedValue: '$100.00' }
// - createCurrencyNumberFormat({ value: 100, formatter: formatPrice, currency: { code: 'USD' }, currencyFormatOptions: { showSymbol: true } }) => { value: 100, formattedValue: 'US$100.00' }
const createCurrencyNumberFormat = ({
  value,
  formatter,
  exchangeRates,
  currency,
  tokenSymbol,
  isExchangeRateBasedOnSymbolCurrency,
  isUsedForTokenPrice,
  currencyFormatOptions,
}: {
  value: number | undefined
  formatter: ValueFormatter
  exchangeRates: ExchangeRatesData
  currency: Currency
  tokenSymbol?: string
  isExchangeRateBasedOnSymbolCurrency?: boolean
  isUsedForTokenPrice?: boolean
  currencyFormatOptions?: FormatTokenPriceOptions
}): CurrencyNumberFormatResult => {
  const processedValue = processAmountValue({
    value,
    exchangeRates,
    currency,
    tokenSymbol,
    isExchangeRateBasedOnSymbolCurrency,
    isUsedForTokenPrice,
    formatter,
  })

  const formattedValue = formatCurrencyValue(processedValue, formatter)

  return {
    value: processedValue,
    formattedValue: currencyFormatOptions
      ? // If currencyFormatOptions are provided, use formatCurrencyAmount
        formatCurrencyAmount(formattedValue, currency, currencyFormatOptions)
      : formattedValue,
  }
}

// Pure function to transform amount object with default values
// Processes AmountObject type with fallback to default parameters
// Example:
// - transformAmountObject({ value: 100, currency: { code: 'EUR' } }, exchangeRates, 'BTC', { code: 'USD' }) => processes with EUR currency
// - transformAmountObject({ value: 100 }, exchangeRates, 'BTC', { code: 'USD' }) => uses USD as default currency
const transformAmountObject = (
  amount: AmountObject<number | undefined>,
  exchangeRates: ExchangeRatesData,
  defaultTokenSymbol?: string,
  defaultCurrency?: Currency,
  defaultValueFormatter?: ValueFormatter,
  defaultCurrencyFormatOptions?: FormatTokenPriceOptions,
  defaultIsExchangeRateBasedOnSymbolCurrency?: boolean,
  defaultIsUsedForTokenPrice?: boolean,
): CurrencyNumberFormatResult => {
  return createCurrencyNumberFormat({
    value: amount.value,
    formatter: amount.valueFormatter || defaultValueFormatter || fiatCurrencyFormat,
    exchangeRates,
    currency: amount.currency || defaultCurrency || DEFAULT_CURRENCY,
    tokenSymbol: amount.tokenSymbol || defaultTokenSymbol,
    isExchangeRateBasedOnSymbolCurrency:
      amount.isExchangeRateBasedOnSymbolCurrency ?? defaultIsExchangeRateBasedOnSymbolCurrency,
    isUsedForTokenPrice: amount.isUsedForTokenPrice ?? defaultIsUsedForTokenPrice,
    currencyFormatOptions: amount.currencyFormatOptions || defaultCurrencyFormatOptions,
  })
}

// Pure function to transform simple amount (number) with default values
// Processes simple number amounts with optional formatting and currency conversion
// Example:
// - transformSimpleAmount(100, exchangeRates, 'BTC', { code: 'USD' }, formatPrice) => converts 100 BTC to USD
// - transformSimpleAmount(100, exchangeRates, undefined, { code: 'USD' }, formatPrice) => treats 100 as USD amount
const transformSimpleAmount = (
  amount: number | undefined,
  exchangeRates: ExchangeRatesData,
  tokenSymbol?: string,
  currency?: Currency,
  valueFormatter?: ValueFormatter,
  currencyFormatOptions?: FormatTokenPriceOptions,
  isExchangeRateBasedOnSymbolCurrency?: boolean,
  isUsedForTokenPrice?: boolean,
): CurrencyNumberFormatResult => {
  return createCurrencyNumberFormat({
    value: amount,
    formatter: valueFormatter || fiatCurrencyFormat,
    exchangeRates,
    currency: currency || DEFAULT_CURRENCY,
    tokenSymbol,
    isExchangeRateBasedOnSymbolCurrency,
    isUsedForTokenPrice,
    currencyFormatOptions,
  })
}

// Pure function to transform amount array with mixed types
// Processes an array of amounts that can be numbers or AmountObjects
// Example:
// - transformAmountArray([100, { value: 200, currency: { code: 'EUR' } }], exchangeRates, 'BTC', { code: 'USD' })
//   => processes both simple number and object amounts with BTC to USD conversion
const transformAmountArray = (
  amounts: Amounts,
  exchangeRates: ExchangeRatesData,
  tokenSymbol?: string,
  currency?: Currency,
  valueFormatter?: ValueFormatter,
  currencyFormatOptions?: FormatTokenPriceOptions,
  isExchangeRateBasedOnSymbolCurrency?: boolean,
  isUsedForTokenPrice?: boolean,
): CurrencyNumberFormatResult[] => {
  return amounts.map(amount => {
    if (typeof amount === 'object' && amount !== null) {
      return transformAmountObject(
        amount,
        exchangeRates,
        tokenSymbol,
        currency,
        valueFormatter,
        currencyFormatOptions,
        isExchangeRateBasedOnSymbolCurrency,
        isUsedForTokenPrice,
      )
    }

    return transformSimpleAmount(
      amount,
      exchangeRates,
      tokenSymbol,
      currency,
      valueFormatter,
      currencyFormatOptions,
      isExchangeRateBasedOnSymbolCurrency,
      isUsedForTokenPrice,
    )
  })
}

/**
 * Convert all kinds of amounts to specified currency from token
 * currency based on token symbol.
 *
 * @param options - options configure
 * @param options.amounts - accepts number list
 * @param options.tokenSymbol - accepts a amount currency's code
 * @param {Currency} [options.currency] - accepts a target currency
 * @param {boolean} [options.isUsedForTokenPrice = false] - is this used for token price? in token price, if
 *  token symbol is same as target currency's code, the finale amount will always be 1
 * @param {boolean} [options.isExchangeRateBasedOnSymbolCurrency = true] - default is true, if true, the calculation will
 *  be `finaleAmount = amount * targetCurrency / amountCurrency`
 *
 * @example
  const { amounts } = useCurrencyAmounts({
    amounts: [cellData],
    valueFormatter: formatPrice,
    tokenSymbol: TYPE_CURRENCY[type].code,
  })
 */
export function useCurrencyAmounts({
  amounts,
  tokenSymbol,
  currency,
  valueFormatter,
  currencyFormatOptions,
  isUsedForTokenPrice = true,
  isExchangeRateBasedOnSymbolCurrency = false,
}: UseCurrencyAmountsOptions) {
  const { selectedCurrency } = useSsrContext()
  const currentCurrency = getCurrencyInfo(selectedCurrency) || DEFAULT_CURRENCY
  const exchangeRates = useExchangeRates()

  const targetCurrency = currency || currentCurrency

  return {
    currency: targetCurrency,
    amounts: transformAmountArray(
      amounts,
      exchangeRates.data,
      tokenSymbol,
      targetCurrency,
      valueFormatter,
      currencyFormatOptions,
      isExchangeRateBasedOnSymbolCurrency,
      isUsedForTokenPrice,
    ),
  }
}
